import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { Box, IconButton, Paper, Typography, useMediaQuery, useTheme } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';

import usePdfDocument from '@/hook/usePdfDocument';
import useTwoPageScale from '@/hook/useTwoPageScale';
import usePdfRender from '@/hook/usePdfRender';

import PdfSpread from '@/TRC/components/PdfView/components/PdfSpread';

import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

const GAP = 20;
const MOBILE_GAP = 0;

const getSpreadStartPage = (pageNumber, isSinglePageMode) => {
	if (isSinglePageMode || pageNumber <= 1) return pageNumber;
	return pageNumber % 2 === 0 ? pageNumber - 1 : pageNumber;
};

const PdfViewer = ({ fileUrl, height = '80vh' }) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
	const isSinglePageMode = isMobile || isTablet;

	const containerRef = useRef(null);
	const leftCanvasRef = useRef(null);
	const rightCanvasRef = useRef(null);

	const { pdf, numPages, docLoading, firstPageSizeRef } = usePdfDocument(fileUrl);

	// Smooth zoom system
	const [visualZoom, setVisualZoom] = useState(1);
	const [renderedZoom, setRenderedZoom] = useState(1);
	const [zoomTimer, setZoomTimer] = useState(null);
	const [initialZoomCalculated, setInitialZoomCalculated] = useState(false);

	const gapValue = isMobile ? MOBILE_GAP : GAP;
	const { baseScale } = useTwoPageScale(containerRef, firstPageSizeRef, gapValue, isSinglePageMode, pdf, isMobile);

	// CSS scale for smooth zoom, effective scale for rendering
	const cssScale = useMemo(() => (renderedZoom ? visualZoom / renderedZoom : visualZoom), [visualZoom, renderedZoom]);
	const effectiveScale = useMemo(() => (baseScale || 0.5) * renderedZoom, [baseScale, renderedZoom]);

	const [currentPage, setCurrentPage] = useState(1);
	const [isFullscreen, setIsFullscreen] = useState(false);
	const [pageLinks, setPageLinks] = useState({ left: [], right: [] });

	// Only start rendering after initial zoom is calculated
	const shouldRender = pdf && initialZoomCalculated;

	const { rendering } = usePdfRender({
		pdf: shouldRender ? pdf : null,
		numPages,
		currentPage,
		effectiveScale,
		canvasRef1: leftCanvasRef,
		canvasRef2: rightCanvasRef
	});

	// Calculate initial zoom to fit BOTH width and height without any scrolling
	useEffect(() => {
		if (!containerRef.current || !firstPageSizeRef.current || !baseScale || initialZoomCalculated) return;

		const containerRect = containerRef.current.getBoundingClientRect();
		const containerWidth = containerRect.width;
		const containerHeight = containerRect.height;

		// Wait for container to have valid dimensions
		if (containerWidth <= 0 || containerHeight <= 0) return;

		// Since baseScale now accounts for both width and height,
		// initial zoom should be 1.0 to show the PDF at the correct size
		setVisualZoom(1);
		setRenderedZoom(1);
		setInitialZoomCalculated(true);
	}, [baseScale, isSinglePageMode, initialZoomCalculated, pdf]);

	// Reset zoom when file changes
	useEffect(() => {
		setVisualZoom(1);
		setRenderedZoom(1);
		setInitialZoomCalculated(false);
	}, [fileUrl]);

	useEffect(() => {
		return () => {
			if (zoomTimer) clearTimeout(zoomTimer);
		};
	}, [zoomTimer]);

	// Smooth zoom handler with debounced re-render
	const handleZoomChange = (_, newZoom) => {
		const clampedZoom = Math.max(0.5, Math.min(3, newZoom));
		setVisualZoom(clampedZoom);

		// Debounce the expensive re-render
		if (zoomTimer) clearTimeout(zoomTimer);
		const timer = setTimeout(() => setRenderedZoom(clampedZoom), 150);
		setZoomTimer(timer);
	};

	const getActiveFullscreenElement = useCallback(() => {
		if (typeof document === 'undefined') return null;
		return (
			document.fullscreenElement ||
			document.webkitFullscreenElement ||
			document.mozFullScreenElement ||
			document.msFullscreenElement ||
			null
		);
	}, []);

	const toggleFullscreen = () => {
		const fullscreenTarget = containerRef.current?.parentElement;
		if (!fullscreenTarget) return;

		const exit =
			document.exitFullscreen ||
			document.webkitExitFullscreen ||
			document.mozCancelFullScreen ||
			document.msExitFullscreen;

		if (getActiveFullscreenElement()) {
			exit?.call(document);
			return;
		}

		const request =
			fullscreenTarget.requestFullscreen ||
			fullscreenTarget.webkitRequestFullscreen ||
			fullscreenTarget.mozRequestFullScreen ||
			fullscreenTarget.msRequestFullscreen;

		request?.call(fullscreenTarget);
	};

	useEffect(() => {
		const handleFullscreenChange = () => {
			setIsFullscreen(!!getActiveFullscreenElement());
		};
		document.addEventListener('fullscreenchange', handleFullscreenChange);
		document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
		return () => {
			document.removeEventListener('fullscreenchange', handleFullscreenChange);
			document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
		};
	}, [getActiveFullscreenElement]);

	const pageIncrement = isSinglePageMode ? 1 : 2;
	const goPrev = () => setCurrentPage(p => Math.max(1, p - pageIncrement));
	const goNext = () =>
		setCurrentPage(p => {
			const nextPage = p + pageIncrement;
			return nextPage <= numPages ? nextPage : p;
		});

	const resolveDestinationPage = useCallback(async dest => {
		if (!pdf || !dest) return null;
		const explicitDest = Array.isArray(dest) ? dest : await pdf.getDestination(dest);
		if (!explicitDest?.length) return null;

		const pageRef = explicitDest[0];
		if (typeof pageRef === 'number') return pageRef + 1;

		try {
			return (await pdf.getPageIndex(pageRef)) + 1;
		} catch {
			return null;
		}
	}, [pdf]);

	const handlePdfLinkClick = useCallback(async dest => {
		const pageNumber = await resolveDestinationPage(dest);
		if (!pageNumber) return;
		setCurrentPage(getSpreadStartPage(Math.min(pageNumber, numPages), isSinglePageMode));
	}, [isSinglePageMode, numPages, resolveDestinationPage]);

	useEffect(() => {
		if (!pdf || !effectiveScale || !initialZoomCalculated) {
			setPageLinks({ left: [], right: [] });
			return;
		}

		let canceled = false;
		const buildPageLinks = async pageNum => {
			if (pageNum > numPages) return [];
			const page = await pdf.getPage(pageNum);
			const viewport = page.getViewport({ scale: effectiveScale });
			const annotations = await page.getAnnotations({ intent: 'display' });

			return annotations
				.filter(annotation => annotation.subtype === 'Link' && (annotation.dest || annotation.url))
				.map((annotation, index) => {
					const rect = viewport.convertToViewportRectangle(annotation.rect);
					const left = Math.min(rect[0], rect[2]);
					const top = Math.min(rect[1], rect[3]);
					const width = Math.abs(rect[0] - rect[2]);
					const height = Math.abs(rect[1] - rect[3]);

					return {
						id: `${pageNum}-${index}`,
						left,
						top,
						width,
						height,
						dest: annotation.dest,
						url: annotation.url
					};
				});
		};

		(async () => {
			const [left, right] = await Promise.all([
				buildPageLinks(currentPage),
				!isSinglePageMode && currentPage + 1 <= numPages ? buildPageLinks(currentPage + 1) : []
			]);

			if (!canceled) setPageLinks({ left, right });
		})();

		return () => {
			canceled = true;
		};
	}, [currentPage, effectiveScale, initialZoomCalculated, isSinglePageMode, numPages, pdf]);

	const getDisplaySize = () => {
		if (firstPageSizeRef.current) {
			const { width, height } = firstPageSizeRef.current;
			return {
				w: Math.max(80, Math.round(width * effectiveScale)),
				h: Math.max(100, Math.round(height * effectiveScale))
			};
		}
		return { w: 420, h: 594 };
	};

	const { w, h } = getDisplaySize();
	const showRightPage = !isSinglePageMode && currentPage + 1 <= numPages;
	const viewerHeight = isFullscreen ? '100%' : isMobile ? 'auto' : height;
	const viewerMinHeight = isFullscreen ? '100%' : isMobile ? 'calc(100vh - 6.5rem)' : height;
	const viewerWidth = isFullscreen ? '100%' : isMobile ? '100vw' : '100%';
	const viewerMaxWidth = isFullscreen ? '100%' : isMobile ? '100vw' : '100%';
	const viewerLeft = !isFullscreen && isMobile ? '50%' : 'auto';
	const viewerTransform = !isFullscreen && isMobile ? 'translateX(-50%)' : 'none';
	let contentPadding;
	if (isFullscreen) {
		contentPadding = { xs: 0, sm: '0.5rem', md: '1rem' };
	} else if (cssScale > 1) {
		contentPadding = { xs: isMobile ? '0.375rem' : '0.5rem', sm: '1rem', md: '2rem' };
	} else {
		contentPadding = { xs: isMobile ? 0 : '0.125rem', sm: '0.5rem' };
	}

	return (
		<Box
			sx={{
				position: 'relative',
				width: viewerWidth,
				maxWidth: viewerMaxWidth,
				height: viewerHeight,
				minHeight: viewerMinHeight,
				borderRadius: isFullscreen ? 0 : 2,
				overflow: 'hidden',
				display: 'flex',
				alignItems: 'center',
				justifyContent: isFullscreen ? 'center' : isMobile ? 'flex-start' : 'center',
				paddingTop: isFullscreen ? 0 : isMobile ? '1rem' : 0,
				margin: 0,
				left: viewerLeft,
				transform: viewerTransform,
				'&:hover .pdf-controls': {
					opacity: 1,
					pointerEvents: 'auto'
				}
			}}
		>
			<IconButton
				onClick={goPrev}
				disabled={currentPage <= 1 || rendering}
				className="pdf-controls"
				sx={{
					position: 'absolute',
					left: { xs: '0.25rem', sm: '0.75rem', md: '1.25rem' },
					top: '50%',
					transform: 'translateY(-50%)',
					color: 'white',
					bgcolor: 'rgba(0,0,0,0.6)',
					width: { xs: '2.5rem', sm: '3.75rem', md: '5rem' },
					height: { xs: '2.5rem', sm: '3.75rem', md: '5rem' },
					p: 0,
					'&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
					'&:disabled': { bgcolor: 'rgba(0,0,0,0.2)', color: 'rgba(255,255,255,0.3)' },
					zIndex: 10,
					opacity: 0,
					pointerEvents: 'none',
					transition: 'opacity 0.3s ease'
				}}
			>
				<ArrowBackIosNewIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.75rem', md: '2.5rem' } }} />
			</IconButton>

			<IconButton
				onClick={goNext}
				disabled={currentPage + pageIncrement > numPages || rendering}
				className="pdf-controls"
				sx={{
					position: 'absolute',
					right: { xs: '0.25rem', sm: '0.75rem', md: '1.25rem' },
					top: '50%',
					transform: 'translateY(-50%)',
					color: 'white',
					bgcolor: 'rgba(0,0,0,0.6)',
					width: { xs: '2.5rem', sm: '3.75rem', md: '5rem' },
					height: { xs: '2.5rem', sm: '3.75rem', md: '5rem' },
					p: 0,
					'&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
					'&:disabled': { bgcolor: 'rgba(0,0,0,0.2)', color: 'rgba(255,255,255,0.3)' },
					zIndex: 10,
					opacity: 0,
					pointerEvents: 'none',
					transition: 'opacity 0.3s ease'
				}}
			>
				<ArrowForwardIosIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.75rem', md: '2.5rem' } }} />
			</IconButton>

			{/* Scrollable container for PDF viewer */}
			<Box
				ref={containerRef}
				sx={{
					position: 'relative',
					width: '100%',
					height: '100%',
					overflow: 'auto'
				}}
			>
				{/* Wrapper to center content when not zoomed, allow full scroll when zoomed */}
				<Box
					sx={{
						minWidth: '100%',
						minHeight: '100%',
						display: 'inline-flex',
						alignItems: isFullscreen ? 'center' : isMobile ? 'flex-start' : 'center',
						justifyContent: isFullscreen ? 'center' : 'center',
						padding: contentPadding,
						boxSizing: 'border-box'
					}}
				>
					{/* PDF Spread with CSS zoom */}
					<Box
						sx={{
							transform: `scale(${cssScale})`,
							transformOrigin: 'center center',
							willChange: 'transform',
							transition: 'transform 0.1s ease-out',
							flexShrink: 0
						}}
					>
						<PdfSpread
							gap={gapValue}
							w={w}
							h={h}
							showRightPage={showRightPage}
							leftCanvasRef={leftCanvasRef}
							rightCanvasRef={rightCanvasRef}
							docLoading={docLoading}
							rendering={rendering}
							leftPageLinks={pageLinks.left}
							rightPageLinks={pageLinks.right}
							onPdfLinkClick={handlePdfLinkClick}
						/>
					</Box>
				</Box>
			</Box>

			<Paper
				elevation={2}
				className="pdf-controls"
				sx={{
					position: 'absolute',
					bottom: { xs: isFullscreen ? '1.5rem' : '0.75rem', sm: '1rem', md: '1.5rem' },
					left: '50%',
					transform: 'translateX(-50%)',
					px: { xs: '0.5rem', sm: '0.625rem', md: '0.75rem' },
					py: { xs: '0.25rem', sm: '0.32rem', md: '0.375rem' },
					display: 'flex',
					alignItems: 'center',
					gap: '0.25rem',
					zIndex: 10,
					bgcolor: '#fff',
					borderRadius: '1.5rem',
					opacity: 0,
					pointerEvents: 'none',
					transition: 'opacity 0.3s ease',
					boxShadow: '0 0.125rem 0.5rem rgba(0,0,0,0.15)'
				}}
			>
				<IconButton
					onClick={() => handleZoomChange(null, Math.max(0.5, visualZoom - 0.1))}
					size="small"
					disabled={visualZoom <= 0.5}
					sx={{
						width: { xs: '1.75rem', sm: '1.875rem', md: '2rem' },
						height: { xs: '1.75rem', sm: '1.9rem', md: '2rem' },
						color: visualZoom <= 0.5 ? '#ccc' : '#5f6368',
						'&:hover': {
							bgcolor: 'rgba(0,0,0,0.04)'
						}
					}}
				>
					<ZoomOutIcon sx={{ fontSize: { xs: '1.125rem', sm: '1.19rem', md: '1.25rem' } }} />
				</IconButton>

				<Typography
					sx={{
						minWidth: { xs: '2.5rem', sm: '2.75rem', md: '3rem' },
						textAlign: 'center',
						fontSize: { xs: '0.75rem', sm: '0.79rem', md: '0.82rem' },
						fontWeight: 500,
						color: '#5f6368',
						userSelect: 'none',
						mx: '0.25rem'
					}}
				>
					{Math.round(visualZoom * 100)}%
				</Typography>

				<IconButton
					onClick={() => handleZoomChange(null, Math.min(3, visualZoom + 0.1))}
					size="small"
					disabled={visualZoom >= 3}
					sx={{
						width: { xs: '1.75rem', sm: '1.875rem', md: '2rem' },
						height: { xs: '1.75rem', sm: '1.875rem', md: '2rem' },
						color: visualZoom >= 3 ? '#ccc' : '#5f6368',
						'&:hover': {
							bgcolor: 'rgba(0,0,0,0.04)'
						}
					}}
				>
					<ZoomInIcon sx={{ fontSize: { xs: '1.125rem', sm: '1.2rem', md: '1.25rem' } }} />
				</IconButton>

				{/* Fullscreen button - only on mobile */}
				{isMobile && (
					<>
						<Box sx={{ width: '0.0625rem', height: '1.25rem', bgcolor: '#e0e0e0', mx: '0.25rem' }} />
						<IconButton
							onClick={toggleFullscreen}
							size="small"
							sx={{
								width: { xs: '1.75rem', sm: '1.875rem', md: '2rem' },
								height: { xs: '1.75rem', sm: '1.875rem', md: '2rem' },
								color: '#5f6368',
								'&:hover': {
									bgcolor: 'rgba(0,0,0,0.04)'
								}
							}}
						>
							{isFullscreen ? (
								<FullscreenExitIcon sx={{ fontSize: { xs: '1.125rem', sm: '1.19rem', md: '1.25rem' } }} />
							) : (
								<FullscreenIcon sx={{ fontSize: { xs: '1.125rem', sm: '1.19rem', md: '1.25rem' } }} />
							)}
						</IconButton>
					</>
				)}
			</Paper>
		</Box>
	);
};

export default PdfViewer;
